import { useCallback } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem'; // [Mới] Import MenuItem
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { Typography } from '@mui/material';

// ----------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tất cả' }, 
  { value: 'public', label: 'Công khai' },
  { value: 'private', label: 'Riêng tư' },
];

type Props = {
  filters: {
    name: string;
    status: string;
    category: string; // [Mới] Thêm field category
  };
  onFilters: (name: string, value: string) => void;
  // [Mới] Nhận danh sách các options thể loại từ cha truyền vào
  categoryOptions: {
    value: string;
    label: string;
  }[];
  onResetFilters: VoidFunction;
  canReset: boolean;
  numSelected: number;
  onDeleteRows: VoidFunction;
  dataCounts: {
    total: number;
    public: number;
    private: number;
  };
};

export default function BlogTableToolbar({
  filters,
  onFilters,
  categoryOptions, // [Mới]
  onResetFilters,
  canReset,
  numSelected,
  onDeleteRows,
  dataCounts,
}: Props) {
  const confirm = useBoolean();

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('name', event.target.value);
    },
    [onFilters]
  );

  // [Mới] Hàm xử lý khi chọn thể loại
  const handleFilterCategory = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('category', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      onFilters('status', newValue);
    },
    [onFilters]
  );

  const handleDeleteConfirm = useCallback(() => {
    onDeleteRows();
    confirm.onFalse();
  }, [confirm, onDeleteRows]);

  return (
    <>
      <Stack
        spacing={2}
        alignItems={{ xs: 'flex-end', md: 'center' }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: 1 }}>
          
          {/* [Mới] Dropdown chọn Thể loại */}
          <TextField
            select
            label="Thể loại"
            value={filters.category}
            onChange={handleFilterCategory}
            sx={{
              width: { xs: 1, md: 200 }, // Responsive width
              textTransform: 'capitalize',
            }}
          >
            {/* Option mặc định chọn tất cả */}
            <MenuItem value="all">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Tất cả thể loại
              </Typography>
            </MenuItem>

            {/* Map qua danh sách categoryOptions truyền vào */}
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            value={filters.name}
            onChange={handleFilterName} 
            placeholder="Tìm kiếm bài viết..."
            sx={{ width: { xs: 1, md: 300 } }} // Responsive width
            InputProps={{  
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Làm mới">
            <Button
              variant="outlined"
              onClick={onResetFilters}
              startIcon={<Iconify icon="solar:refresh-bold" />}
              sx={{ minWidth: 100 }}
            >
              Làm mới
            </Button>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          {numSelected > 0 && (
            <Button
              color="error"
              variant="contained"
              onClick={confirm.onTrue}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              sx={{ minWidth: 120 }}
            >
              Xóa ({numSelected})
            </Button>
          )}

          {canReset && (
            <Button
              color="error"
              variant="outlined"
              onClick={onResetFilters}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              sx={{ minWidth: 120 }}
            >
              Xóa bộ lọc
            </Button>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={filters.status}
        onChange={handleFilterStatus}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {STATUS_OPTIONS.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="end"
            value={tab.value}
            label={tab.label}
            icon={
              <Label
                variant={
                  ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                }
                color={
                  (tab.value === 'public' && 'success') ||
                  (tab.value === 'private' && 'default') ||
                  'default'
                }
              >
                {tab.value === 'all' && dataCounts.total}
                {tab.value === 'public' && dataCounts.public}
                {tab.value === 'private' && dataCounts.private}
              </Label>
            }
          />
        ))}
      </Tabs>

      <Dialog open={confirm.value} onClose={confirm.onFalse}>
        <DialogTitle>Xóa bài viết</DialogTitle>
        
        <DialogContent sx={{ color: 'text.secondary' }}>
          Bạn có chắc chắn muốn xóa {numSelected} bài viết đã chọn không? 
          Hành động này không thể hoàn tác. 
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={confirm.onFalse}>
            Hủy
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteConfirm}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}